import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { LibrarySlicePersistenceService } from '../core/library-slice-persistence.service';
import { LibrarySliceService } from '../core/library-slice.service';
import { DataSlice } from 'src/app/state/library/library.model';
import { LibraryState } from 'src/app/state/library/library.state';
import { SliceChartDataResolver } from './slice-chart-data-resolver.service';
import { AuthState } from 'src/app/state/auth/auth.state';

/** Refreshes running at once. Each one rebuilds a dashboard pipeline on the main thread. */
const REFRESH_CONCURRENCY = 2;

/**
 * Refreshes live slice previews on demand. Slice cards request a refresh when
 * they scroll into view, so a dataset rebuild never re-renders every slice at
 * once: only stale slices the user actually looks at are refreshed, through a
 * small queue, in the order they appeared.
 */
@Injectable({ providedIn: 'root' })
export class SlicePreviewRefreshService {

  /** Slice ids waiting to refresh, in request order. */
  private _queue: string[] = [];
  /** Slice ids queued or refreshing. */
  private readonly _pending = new Set<string>();
  /** Slice ids already tried during this library visit; a failed refresh isn't retried on every scroll. */
  private readonly _attempted = new Set<string>();
  private _running = 0;

  private readonly _pending$ = new BehaviorSubject<ReadonlySet<string>>(new Set());

  constructor(
    private store: Store,
    private resolver: SliceChartDataResolver,
    private librarySliceService: LibrarySliceService,
    private persistence: LibrarySlicePersistenceService,
  ) {}

  /** Ids of slices queued or refreshing. */
  get pending$(): Observable<ReadonlySet<string>> {
    return this._pending$.asObservable();
  }

  /**
   * A live slice is stale when its dataset was rebuilt after its preview.
   * Slices whose dataset the user can no longer access are never stale: they can't be refreshed.
   */
  isStale(slice: DataSlice): boolean {
    if (!slice.live) {
      return false;
    }
    const dataset = this.store.selectSnapshot(AuthState.accessibleDatasets).get(slice.datasetName);
    if (!dataset) {
      return false;
    }
    const datasetBuiltAt = Date.parse(dataset.built_at ?? '');
    if (Number.isNaN(datasetBuiltAt)) {
      return false;
    }
    const previewBuiltAt = Date.parse(slice.datasetBuiltAt);
    return Number.isNaN(previewBuiltAt) || previewBuiltAt < datasetBuiltAt;
  }

  /** Queue a refresh for a slice that came into view. No-op unless it is stale and not already handled. */
  request(slice: DataSlice): void {
    if (this._pending.has(slice.id) || this._attempted.has(slice.id) || !this.isStale(slice)) {
      return;
    }
    this._queue.push(slice.id);
    this._pending.add(slice.id);
    this._emit();
    this._drain();
  }

  /** Drop queued refreshes (running ones finish) and forget attempts. Call when leaving the library page. */
  cancelPending(): void {
    this._queue.forEach((id) => this._pending.delete(id));
    this._queue = [];
    this._attempted.clear();
    this._emit();
  }

  private _drain(): void {
    while (this._running < REFRESH_CONCURRENCY && this._queue.length > 0) {
      const id = this._queue.shift()!;
      this._running++;
      this._run(id).finally(() => {
        this._running--;
        this._pending.delete(id);
        this._emit();
        this._drain();
      });
    }
  }

  private async _run(sliceId: string): Promise<void> {
    // Let the browser paint between pipelines.
    await new Promise((resolve) => setTimeout(resolve));

    const slice = this._currentSlice(sliceId);
    if (!slice || !this.isStale(slice)) {
      return;
    }
    this._attempted.add(sliceId);

    try {
      const chartData = await this.resolver.resolveChartData(slice);
      if (!chartData?.length || !this._currentSlice(sliceId)) {
        return;
      }

      const previewSeries = this.librarySliceService.buildPreviewSeries(
        chartData,
        slice.dashboardId,
        slice.filterValues,
        slice.shellId,
      );
      const datasetBuiltAt = this.store
        .selectSnapshot(AuthState.accessibleDatasets)
        .get(slice.datasetName)?.built_at;
      if (!datasetBuiltAt) {
        return;
      }

      await firstValueFrom(this.persistence.updatePreview(sliceId, previewSeries, datasetBuiltAt));
    } catch (error) {
      console.warn('[SlicePreviewRefreshService] Failed to refresh slice', sliceId, error);
    }
  }

  private _currentSlice(sliceId: string): DataSlice | undefined {
    return this.store.selectSnapshot(LibraryState.slices).find((s) => s.id === sliceId);
  }

  private _emit(): void {
    this._pending$.next(new Set(this._pending));
  }
}
